import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import React, { ComponentProps, useMemo } from 'react';
import styled from 'styled-components';

import TimedOrCategorySearchCard from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import PageTitle from '@/components/PageTitle/PageTitle';
import PaginateSortFilter from '@/components/Pagination/PaginateSortFilter';
import { itemSortOrders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getIsLoadingStorefront, getStorefrontAgentName } from '@/redux/modules/storefront/storefront.selectors';
import { addImageParams, getAuctioneerLogoUrl, imagePlaceholder } from '@/utils/urls';

import StorefrontPageHeaderMeta from './StorefrontPageHeaderMeta';

type Props = {
    buyNow: boolean;
    changePaginationPageSize: Function;
    containsBuyNowItems: boolean;
    filteredItemCount: number;
    isSearch: boolean;
    items: any[];
    logoId: string;
    pageSize: number;
    sellerId: number;
    sortOrder: string;
    totalItemCount: number;
    viewData: any;
};

const StorefrontPage = ({
    buyNow,
    changePaginationPageSize,
    containsBuyNowItems,
    filteredItemCount,
    isSearch,
    items,
    logoId,
    pageSize,
    sellerId,
    sortOrder,
    totalItemCount,
    viewData,
}: Props) => {
    const { formatMessage } = useIntl();

    const { page, searchTerms } = viewData;

    const filteredCount = isSearch ? filteredItemCount : totalItemCount;

    const logoUrl = logoId ? getAuctioneerLogoUrl(logoId) : imagePlaceholder;
    const src = addImageParams({ height: 83, url: logoUrl });
    const agentName = useAppSelector(getStorefrontAgentName);
    const isLoading = useAppSelector((state) => getIsLoadingStorefront(state, sellerId));

    const pageTitle = useMemo((): ComponentProps<typeof PageTitle>['title'] => {
        if (agentName) {
            return agentName;
        }

        // no name, show sellerId title instead
        return (
            <FormattedMessage
                id="seller.number"
                values={{ sellerId }}
            />
        );
    }, [sellerId, agentName]);

    return (
        <Container>
            <StorefrontPageHeaderMeta
                firstItemId={items?.[0]?.itemId}
                itemCount={items.length}
                sellerId={sellerId}
            />
            <SellerContainer>
                {logoId && (
                    <SellerLogo>
                        <img
                            // marked as decorative since the description
                            // would be similar to the page title,
                            // which will always be read out
                            alt=""
                            role="presentation"
                            src={src}
                        />
                    </SellerLogo>
                )}
                <SellerTitle>
                    <PageTitle title={pageTitle} />
                </SellerTitle>
            </SellerContainer>
            <StyledPaginateSortFilter
                buyNow={buyNow}
                buyNowFilterAvailable={containsBuyNowItems}
                changePaginationPageSize={changePaginationPageSize}
                empty={totalItemCount === 0}
                emptyMessage={<FormattedMessage id={isLoading ? 'seller.notLoaded' : 'seller.empty'} />}
                filteredItemCount={filteredCount}
                page={page}
                pageSize={pageSize}
                searchTerms={searchTerms}
                sortOptions={itemSortOrders}
                sortOrder={sortOrder}
                totalItemsCount={totalItemCount}
                type={formatMessage({ id: 'lots' })}
            >
                <FluidItemGrid
                    itemCardOption={TimedOrCategorySearchCard}
                    itemIds={items.map((item) => item.itemId)}
                />
            </StyledPaginateSortFilter>
        </Container>
    );
};

export default StorefrontPage;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 0 auto;
    margin: 20px 0;
`;

const SellerContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-self: center;
    margin-bottom: 20px;
    max-width: 1200px;
    width: calc(100% - 40px);
`;

const SellerLogo = styled.div`
    align-self: left;
    padding-right: 15px;
    border-right: 1px solid ${({ theme }) => theme.colors.grey500};
    flex: 0 0 auto;
    height: 83px;

    img {
        height: 100%;
    }
`;

const SellerTitle = styled.div`
    margin-left: 30px;
    justify-content: flex-start;
    display: flex;
    align-items: center;
    flex: 1 1 auto;

    && {
        h1 {
            margin: 0;
        }
    }
`;

const StyledPaginateSortFilter = styled(PaginateSortFilter)`
    margin: 0 20px 30px;
    width: calc(100% - 40px);
`;
